'use client'

import { useTranslations } from '@/lib/next-shim'

import PlacesAutocomplete from '@/components/ui/comboboxes/PlacesAutocomplete'

import { useAtHomeAddress } from '../model/useAtHomeAddress'
import type { AtHomeAddressResult, ProfileAddress, SavedAddress } from '../model/types'
import { AccessDetailsCollapsible } from './AccessDetailsCollapsible'
import { ManualAddressForm } from './ManualAddressForm'
import { SaveAddressCheckbox } from './SaveAddressCheckbox'
import { SavedAddressesView } from './SavedAddressesView'
import { confirmButtonClassName, tabClassName } from '../lib/classNames'

type AtHomeAddressPickerProps = {
  savedAddresses: SavedAddress[]
  profileAddress?: ProfileAddress | null
  isLoggedIn?: boolean
  onSelect: (result: AtHomeAddressResult) => void
  onDeleteAddress?: (addressId: string) => void
  onSaveAddress?: (address: Omit<SavedAddress, '_id'>) => void
}

export function AtHomeAddressPicker({
  savedAddresses,
  profileAddress,
  isLoggedIn = false,
  onSelect,
  onDeleteAddress,
  onSaveAddress,
}: AtHomeAddressPickerProps) {
  const t = useTranslations('address')

  const {
    hasSavedAddresses,
    hasProfileAddress,
    profileAlreadySaved,
    canSaveAddress,
    mode,
    addressMode,
    manualAddress,
    selectedAddress,
    details,
    showDetails,
    saveAddress,
    newAddressLabel,
    setManualAddress,
    setDetails,
    setSaveAddress,
    setNewAddressLabel,
    toggleDetails,
    goToNew,
    goToList,
    switchAddressMode,
    selectSavedAddress,
    selectProfileAddress,
    selectSearchResult,
    confirm,
    confirmManual,
  } = useAtHomeAddress({ savedAddresses, profileAddress, isLoggedIn, onSelect, onSaveAddress })

  const saveCheckbox = canSaveAddress ? (
    <SaveAddressCheckbox checked={saveAddress} onCheckedChange={setSaveAddress} label={newAddressLabel} onLabelChange={setNewAddressLabel} />
  ) : null

  const accessDetails = <AccessDetailsCollapsible open={showDetails} onToggle={toggleDetails} value={details} onChange={setDetails} />

  return (
    <div className="space-y-4">
      {mode === 'list' && hasSavedAddresses && (
        <SavedAddressesView
          savedAddresses={savedAddresses}
          onSelect={selectSavedAddress}
          onDelete={onDeleteAddress}
          profileAddress={profileAddress}
          showProfileQuickPick={hasProfileAddress && !profileAlreadySaved}
          onSelectProfile={selectProfileAddress}
          onNewAddress={goToNew}
        />
      )}

      {mode === 'new' && (
        <>
          {hasSavedAddresses && (
            <button type="button" onClick={goToList} className="text-sm text-indigo-600 hover:text-indigo-500">
              ← {t('savedAddresses')}
            </button>
          )}

          {/* Same-line segmented control: address search vs. manual entry */}
          <div role="tablist" className="grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1">
            <button
              type="button"
              role="tab"
              aria-selected={addressMode === 'search'}
              onClick={() => switchAddressMode('search')}
              className={tabClassName(addressMode === 'search')}
            >
              {t('useAutocomplete')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={addressMode === 'manual'}
              onClick={() => switchAddressMode('manual')}
              className={tabClassName(addressMode === 'manual')}
            >
              {t('enterManually')}
            </button>
          </div>

          {addressMode === 'search' ? (
            <>
              <PlacesAutocomplete hideManualEntry onSelect={selectSearchResult} />
              {selectedAddress && (
                <div className="space-y-4">
                  {accessDetails}
                  {saveCheckbox}
                  <button type="button" onClick={confirm} className={confirmButtonClassName}>
                    {t('confirmAddress')}
                  </button>
                </div>
              )}
            </>
          ) : (
            <ManualAddressForm value={manualAddress} onChange={setManualAddress} details={details} onDetailsChange={setDetails} onConfirm={confirmManual}>
              {saveCheckbox}
            </ManualAddressForm>
          )}
        </>
      )}

      {/* Saved-address selection: access details + confirm */}
      {mode === 'list' && selectedAddress && (
        <div className="space-y-4">
          {accessDetails}
          <button type="button" onClick={confirm} className={confirmButtonClassName}>
            {t('confirmAddress')}
          </button>
        </div>
      )}
    </div>
  )
}
